import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { keys } from "../queryKeys.js";
import * as api from "../api/workspaces.js";

export function useCurrentWorkspace(options = {}) {
  return useQuery({
    queryKey: ["workspace", "current"],
    queryFn: api.fetchCurrentWorkspace,
    ...options,
  });
}

export function useUpdateCurrentWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.updateCurrentWorkspace,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspace"] }),
  });
}

export function useMembers(options = {}) {
  return useQuery({
    queryKey: keys.members.list,
    queryFn: api.fetchMembers,
    ...options,
  });
}

export function useUpdateMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }) => api.updateMemberRole(userId, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.members.all }),
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.removeMember,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.members.all }),
  });
}

export function useWorkspaceInvitations(options = {}) {
  return useQuery({
    queryKey: ["workspace-invitations"],
    queryFn: api.fetchWorkspaceInvitations,
    ...options,
  });
}

export function useCreateWorkspaceInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createWorkspaceInvitation,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspace-invitations"] }),
  });
}

export function useRevokeWorkspaceInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.revokeWorkspaceInvitation,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspace-invitations"] }),
  });
}

export function useChildWorkspaces(options = {}) {
  return useQuery({
    queryKey: ["workspaces", "children"],
    queryFn: api.fetchChildWorkspaces,
    ...options,
  });
}

export function useCreateChildWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createChildWorkspace,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspaces", "children"] }),
  });
}

export function useDeleteChildWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteChildWorkspace,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspaces", "children"] }),
  });
}

export function useChildMembers(id, options = {}) {
  return useQuery({
    queryKey: ["workspaces", "children", id, "members"],
    queryFn: () => api.fetchChildMembers(id),
    enabled: !!id,
    ...options,
  });
}

export function useAddChildMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.addChildMember(id, data),
    onSuccess: (_d, { id }) =>
      qc.invalidateQueries({ queryKey: ["workspaces", "children", id, "members"] }),
  });
}
